import React, { useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import PageShell from "../dashboard/PageShell";
import {
  Plus,
  Search,
  FileDown,
  Pencil,
  Trash2,
  UserPlus,
  ChevronDown,
  Eye,
} from "lucide-react";

interface DelegationItem {
  id: string;
  stt: number;
  function: string;
  delegationName: string;
  country: string;
  numberOfPeople: number;
  headOfDelegation: string;
  memberName: string;
  memberUnit: string;
  vietnamesePartnerUnit: string;
  purpose: string;
  entryDate: string;
  exitDate: string;
  cooperationContent: string;
  basis: string;
  totalFunding: string;
  fundingSource: string;
  notes: string;
}

const mockData: DelegationItem[] = [
  {
    id: "1",
    stt: 1,
    function: "- Chọn -",
    delegationName: "Đoàn 1",
    country: "Việt Nam",
    numberOfPeople: 0,
    headOfDelegation: "Nguyễn Văn A",
    memberName: "Trần Thị B",
    memberUnit: "Tổ chức 1",
    vietnamesePartnerUnit: "Phòng Kế hoạch khoa học",
    purpose: "Tham gia chương trình đào tạo",
    entryDate: "28/03/2022",
    exitDate: "06/04/2022",
    cooperationContent: "",
    basis: "",
    totalFunding: "90.000.000 đ",
    fundingSource: "Ngân sách khác",
    notes: "fbf",
  },
];

const IncomingDelegations: React.FC = () => {
  const navigate = useNavigate();
  const [selectedItems, setSelectedItems] = useState<number[]>([]);
  const [openMenuDelegationId, setOpenMenuDelegationId] = useState<
    string | null
  >(null);
  const [menuPlacement, setMenuPlacement] = useState<"bottom" | "top">(
    "bottom"
  );
  const [buttonPosition, setButtonPosition] = useState<{
    left: number;
    top: number;
    right: number;
    bottom: number;
  } | null>(null);
  const dropdownRefs = useRef<{ [key: string]: HTMLDivElement | null }>({});
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [itemToDelete, setItemToDelete] = useState<DelegationItem | null>(null);
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (openMenuDelegationId) {
        const currentRef = dropdownRefs.current[openMenuDelegationId];
        if (currentRef && !currentRef.contains(event.target as Node)) {
          setOpenMenuDelegationId(null);
        }
      }
    };

    const handleScroll = () => {
      if (openMenuDelegationId && buttonPosition) {
        // Update position on scroll to keep menu attached to button
        const button = document.querySelector(
          `[data-delegation-id="${openMenuDelegationId}"]`
        ) as HTMLButtonElement;
        if (button) {
          const rect = button.getBoundingClientRect();
          setButtonPosition({
            left: rect.left,
            top: rect.top,
            right: rect.right,
            bottom: rect.bottom,
          });
        }
      }
    };

    if (openMenuDelegationId) {
      document.addEventListener("mousedown", handleClickOutside);
      window.addEventListener("scroll", handleScroll, true);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      window.removeEventListener("scroll", handleScroll, true);
    };
  }, [openMenuDelegationId, buttonPosition]);

  const handleViewDetail = (delegationId: string) => {
    console.log("Viewing details for delegation:", delegationId);
    navigate(`/cooperation/delegation-detail/${delegationId}`);
  };

  const handleEdit = (delegationId: string) => {
    console.log("Editing delegation:", delegationId);
    navigate(`/edit-delegation/${delegationId}`);
  };

  const handleAddMember = (delegationId: string) => {
    console.log("Adding member to delegation:", delegationId);
    navigate("/cooperation/delegation/add-member");
  };

  return (
    <PageShell title="Đoàn vào">
      <div className="space-y-6">
        {/* Header Section */}
        <div className="flex justify-between items-center">
          <h1 className="text-xl font-semibold text-gray-800">Đoàn vào</h1>
          <button
            onClick={() => navigate("/add-new-delegation")}
            className="bg-green-600 text-white px-3 py-2 rounded text-sm font-medium hover:bg-green-700 transition-colors flex items-center space-x-2"
          >
            <Plus className="h-4 w-4" />
            <span>Thêm mới Đoàn</span>
          </button>
        </div>

        {/* Search and Filter Section */}
        <div className="bg-white rounded-lg p-4">
          <div className="space-y-4">
            {/* Row 1 - Search input + Search button */}
            <div className="flex items-center space-x-4">
              <div className="relative w-96">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                <input
                  type="text"
                  placeholder="Tìm kiếm đoàn vào"
                  className="w-full pl-10 pr-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              <button className="bg-green-600 text-white px-3 py-2 rounded text-sm font-medium hover:bg-green-700 transition-colors">
                Tìm kiếm
              </button>
            </div>

            {/* Row 2 - Filter dropdowns */}
            <div className="flex items-center space-x-2">
              <select className="border border-gray-300 rounded-md px-2 py-2 text-sm bg-white w-36">
                <option>- Quốc gia -</option>
              </select>
              <select className="border border-gray-300 rounded-md px-2 py-2 text-sm bg-white w-36">
                <option>- Đơn vị tiếp đón -</option>
              </select>
              <select className="border border-gray-300 rounded-md px-2 py-2 text-sm bg-white w-36">
                <option>- Tổ chức -</option>
              </select>
              <select className="border border-gray-300 rounded-md px-2 py-2 text-sm bg-white w-36">
                <option>- Mục đích -</option>
              </select>
              <div className="flex space-x-2">
                <input
                  id="fromDate"
                  type="date"
                  value={fromDate}
                  onChange={(e) => setFromDate(e.target.value)}
                  className="w-36 border border-gray-300 rounded-md px-2 py-2 text-sm bg-white"
                />
                <input
                  id="toDate"
                  type="date"
                  value={toDate}
                  onChange={(e) => setToDate(e.target.value)}
                  className="w-36 border border-gray-300 rounded-md px-2 py-2 text-sm bg-white"
                />
              </div>
            </div>

            {/* Row 3 - Export button */}
            <div className="flex justify-start">
              <button className="bg-orange-500 text-white px-3 py-2 rounded text-sm font-medium hover:bg-orange-600 transition-colors flex items-center space-x-2">
                <FileDown className="h-4 w-4" />
                <span>Xuất ra file</span>
              </button>
            </div>
          </div>
        </div>

        {/* Data Table */}
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                {/* First header row */}
                <tr>
                  <th
                    className="px-2 py-3 text-left text-xs font-bold text-black uppercase tracking-wider border-r border-gray-200 w-8"
                    rowSpan={2}
                  >
                    STT
                  </th>
                  <th
                    className="px-2 py-3 text-left text-xs font-bold text-black uppercase tracking-wider border-r border-gray-200 w-24"
                    rowSpan={2}
                  >
                    Tên đoàn
                  </th>
                  <th
                    className="px-2 py-3 text-left text-xs font-bold text-black uppercase tracking-wider border-r border-gray-200 w-20"
                    rowSpan={2}
                  >
                    Quốc gia
                  </th>
                  <th
                    className="px-2 py-3 text-left text-xs font-bold text-black uppercase tracking-wider border-r border-gray-200 w-16"
                    rowSpan={2}
                  >
                    Số người
                  </th>
                  <th
                    className="px-2 py-3 text-left text-xs font-bold text-black uppercase tracking-wider border-r border-gray-200 w-20"
                    rowSpan={2}
                  >
                    Trưởng đoàn
                  </th>
                  <th
                    className="px-2 py-3 text-center text-xs font-bold text-black uppercase tracking-wider border-r border-gray-200 w-32"
                    colSpan={2}
                  >
                    Thành phần
                  </th>
                  <th
                    className="px-2 py-3 text-left text-xs font-bold text-black uppercase tracking-wider border-r border-gray-200 w-28"
                    rowSpan={2}
                  >
                    ĐV phối hợp phía VN
                  </th>
                  <th
                    className="px-2 py-3 text-left text-xs font-bold text-black uppercase tracking-wider border-r border-gray-200 w-24"
                    rowSpan={2}
                  >
                    Mục đích
                  </th>
                  <th
                    className="px-2 py-3 text-center text-xs font-bold text-black uppercase tracking-wider border-r border-gray-200 w-24"
                    colSpan={2}
                  >
                    Thời gian
                  </th>
                  <th
                    className="px-2 py-3 text-left text-xs font-bold text-black uppercase tracking-wider border-r border-gray-200 w-20"
                    rowSpan={2}
                  >
                    Nội dung hợp tác
                  </th>
                  <th
                    className="px-2 py-3 text-left text-xs font-bold text-black uppercase tracking-wider border-r border-gray-200 w-16"
                    rowSpan={2}
                  >
                    Căn cứ
                  </th>
                  <th
                    className="px-2 py-3 text-center text-xs font-bold text-black uppercase tracking-wider border-r border-gray-200 w-24"
                    colSpan={2}
                  >
                    Kinh phí
                  </th>
                  <th
                    className="px-2 py-3 text-left text-xs font-bold text-black uppercase tracking-wider border-r border-gray-200 w-16"
                    rowSpan={2}
                  >
                    Ghi chú
                  </th>
                  <th
                    className="px-2 py-3 text-left text-xs font-bold text-black uppercase tracking-wider w-20"
                    rowSpan={2}
                  >
                    Chức năng
                  </th>
                </tr>
                {/* Second header row */}
                <tr>
                  <th className="px-2 py-3 text-left text-xs font-bold text-black uppercase tracking-wider border-r border-gray-200 w-16">
                    Họ Tên
                  </th>
                  <th className="px-2 py-3 text-left text-xs font-bold text-black uppercase tracking-wider border-r border-gray-200 w-16">
                    Đơn vị
                  </th>
                  <th className="px-2 py-3 text-left text-xs font-bold text-black uppercase tracking-wider border-r border-gray-200 w-12">
                    Nhập cảnh
                  </th>
                  <th className="px-2 py-3 text-left text-xs font-bold text-black uppercase tracking-wider border-r border-gray-200 w-12">
                    Xuất cảnh
                  </th>
                  <th className="px-2 py-3 text-left text-xs font-bold text-black uppercase tracking-wider border-r border-gray-200 w-12">
                    Tổng
                  </th>
                  <th className="px-2 py-3 text-left text-xs font-bold text-black uppercase tracking-wider border-r border-gray-200 w-12">
                    Nguồn kinh phí
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {mockData.map((item) => (
                  <tr key={item.id} className="hover:bg-gray-50">
                    <td className="px-2 py-4 whitespace-nowrap text-sm font-medium text-gray-900 border-r border-gray-200 w-8">
                      {item.stt}
                    </td>
                    <td className="px-2 py-4 text-sm text-gray-900 border-r border-gray-200 w-24">
                      <div className="font-medium text-[#337ab7] text-[14px] leading-tight">
                        {item.delegationName}
                      </div>
                    </td>
                    <td className="px-2 py-4 text-sm text-gray-900 border-r border-gray-200 w-20">
                      {item.country}
                    </td>
                    <td className="px-2 py-4 text-sm text-gray-900 border-r border-gray-200 w-16 text-center">
                      {item.numberOfPeople}
                    </td>
                    <td className="px-2 py-4 text-sm text-gray-900 border-r border-gray-200 w-20">
                      {item.headOfDelegation}
                    </td>
                    <td className="px-2 py-4 text-sm text-gray-900 border-r border-gray-200 w-16">
                      {item.memberName}
                    </td>
                    <td className="px-2 py-4 text-sm text-gray-900 border-r border-gray-200 w-16">
                      {item.memberUnit}
                    </td>
                    <td className="px-2 py-4 text-sm text-gray-900 border-r border-gray-200 w-28">
                      <div className="leading-tight">
                        {item.vietnamesePartnerUnit}
                      </div>
                    </td>
                    <td className="px-2 py-4 text-sm text-gray-900 border-r border-gray-200 w-24">
                      <div className="leading-tight">{item.purpose}</div>
                    </td>
                    <td className="px-2 py-4 text-sm text-gray-900 border-r border-gray-200 w-12 text-center">
                      {item.entryDate}
                    </td>
                    <td className="px-2 py-4 text-sm text-gray-900 border-r border-gray-200 w-12 text-center">
                      {item.exitDate}
                    </td>
                    <td className="px-2 py-4 text-sm text-gray-900 border-r border-gray-200 w-20">
                      {item.cooperationContent || (
                        <span className="text-gray-400">-</span>
                      )}
                    </td>
                    <td className="px-2 py-4 text-sm text-gray-900 border-r border-gray-200 w-16">
                      {item.basis || <span className="text-gray-400">-</span>}
                    </td>
                    <td className="px-2 py-4 text-sm text-gray-900 border-r border-gray-200 w-12 text-center">
                      {item.totalFunding}
                    </td>
                    <td className="px-2 py-4 text-sm text-gray-900 border-r border-gray-200 w-12">
                      <div className="leading-tight">{item.fundingSource}</div>
                    </td>
                    <td className="px-2 py-4 text-sm text-gray-900 border-r border-gray-200 w-16">
                      {item.notes}
                    </td>
                    <td className="px-2 py-4 whitespace-nowrap border-r border-gray-200 w-20">
                      <div
                        className="relative inline-block"
                        ref={(el) => {
                          dropdownRefs.current[item.id] = el;
                        }}
                      >
                        <button
                          data-delegation-id={item.id}
                          className="bg-[#337ab7] text-white px-2 py-1 rounded text-xs font-medium hover:bg-[#2e6da4] transition-colors flex items-center space-x-1"
                          onClick={(e) => {
                            const target = e.currentTarget as HTMLButtonElement;
                            const rect = target.getBoundingClientRect();
                            const estimatedMenuHeight = 200; // approx height for 4 items with padding
                            const spaceBelow = window.innerHeight - rect.bottom;
                            // More aggressive flipping - flip if less than 200px space below
                            const willOverflowBottom = spaceBelow < 200;
                            setMenuPlacement(
                              willOverflowBottom ? "top" : "bottom"
                            );
                            setButtonPosition({
                              left: rect.left,
                              top: rect.top,
                              right: rect.right,
                              bottom: rect.bottom,
                            });
                            setOpenMenuDelegationId(
                              openMenuDelegationId === item.id ? null : item.id
                            );
                          }}
                        >
                          <span>Chọn</span>
                          <ChevronDown className="h-3 w-3" />
                        </button>
                        {openMenuDelegationId === item.id &&
                          createPortal(
                            <div
                              className={
                                `fixed w-48 bg-white rounded-md shadow-lg border border-gray-200 z-[99999] ` +
                                (menuPlacement === "bottom"
                                  ? "mt-2"
                                  : "bottom-full mb-2")
                              }
                              onMouseDown={(e) => {
                                // Prevent the global mousedown outside handler from closing
                                // the menu before the anchor click can navigate
                                e.stopPropagation();
                              }}
                              style={
                                buttonPosition
                                  ? {
                                      left: `${buttonPosition.right - 200}px`, // 200px = w-48 + 16px offset
                                      top:
                                        menuPlacement === "bottom"
                                          ? `${buttonPosition.bottom + 0}px`
                                          : `${buttonPosition.top - 200}px`,
                                      backgroundColor: "white",
                                      zIndex: 99999,
                                    }
                                  : {}
                              }
                            >
                              <div className="py-1 bg-white">
                                <button
                                  onClick={() => {
                                    handleViewDetail(item.id);
                                    setOpenMenuDelegationId(null);
                                  }}
                                  className="flex items-center gap-2 w-full text-left px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-100 transition-colors border-b border-gray-100 last:border-b-0 bg-white"
                                >
                                  <Eye className="h-4 w-4 text-blue-600" />
                                  <span>Chi tiết</span>
                                </button>
                                <button
                                  onClick={() => {
                                    handleEdit(item.id);
                                    setOpenMenuDelegationId(null);
                                  }}
                                  className="flex items-center gap-2 w-full text-left px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-100 transition-colors border-b border-gray-100 last:border-b-0 bg-white"
                                >
                                  <Pencil className="h-4 w-4 text-amber-600" />
                                  <span>Sửa</span>
                                </button>
                                <button
                                  onClick={() => {
                                    handleAddMember(item.id);
                                    setOpenMenuDelegationId(null);
                                  }}
                                  className="flex items-center gap-2 w-full text-left px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-100 transition-colors border-b border-gray-100 last:border-b-0 bg-white"
                                >
                                  <UserPlus className="h-4 w-4 text-green-600" />
                                  <span>Thêm thành viên</span>
                                </button>
                                <button
                                  onClick={() => {
                                    setItemToDelete(item);
                                    setShowDeleteConfirm(true);
                                    setOpenMenuDelegationId(null);
                                  }}
                                  className="flex items-center gap-2 w-full text-left px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-100 transition-colors border-b border-gray-100 last:border-b-0 bg-white"
                                >
                                  <Trash2 className="h-4 w-4 text-red-600" />
                                  <span>Xóa</span>
                                </button>
                              </div>
                            </div>,
                            document.body
                          )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination inside the table container */}
          <div className="flex justify-between items-center p-4 border-t border-gray-200">
            <div className="text-sm font-medium text-gray-700">
              Hiển thị <span className="font-bold">1</span> đến{" "}
              <select className="font-bold text-blue-600 border-none bg-transparent focus:ring-0 focus:outline-none text-sm">
                <option>10</option>
                <option>20</option>
                <option>50</option>
                <option>100</option>
              </select>{" "}
              trong <span className="font-bold">10000</span> kết quả
            </div>
            <div className="flex space-x-2">
              <button className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50">
                &lt;
              </button>
              <button className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50">
                1
              </button>
              <button className="px-3 py-2 text-sm font-medium text-white bg-[#337ab7] border border-[#337ab7] rounded-md">
                2
              </button>
              <button className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50">
                3
              </button>
              <button className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50">
                &gt;
              </button>
            </div>
          </div>
        </div>
      </div>
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-40 z-50 flex items-center justify-center">
          <div className="bg-white rounded-md p-4 max-w-sm w-full mx-4">
            <h3 className="text-base font-semibold text-gray-900 mb-2">
              Xác nhận xóa
            </h3>
            <p className="text-xs text-gray-600 mb-3">
              Bạn có chắc muốn xóa đoàn{" "}
              <span className="font-semibold text-gray-900">
                {itemToDelete?.delegationName}
              </span>
              ?
            </p>
            <div className="flex justify-end gap-2">
              <button
                onClick={() => {
                  setShowDeleteConfirm(false);
                  setItemToDelete(null);
                }}
                className="px-3 py-1.5 text-sm font-medium text-gray-700 bg-gray-100 rounded hover:bg-gray-200"
              >
                Hủy
              </button>
              <button
                onClick={() => {
                  // TODO: delete item when backend integrated
                  setShowDeleteConfirm(false);
                  setItemToDelete(null);
                }}
                className="px-3 py-1.5 text-sm font-medium text-white bg-red-600 rounded hover:bg-red-700"
              >
                Xóa
              </button>
            </div>
          </div>
        </div>
      )}
    </PageShell>
  );
};

export default IncomingDelegations;
